<template>
  <SupportPageCard
    page-class="work-temp-page"
    :title="pageTitle"
    :show-back="!hideBackBtn"
    @back="goBack"
  >
    <van-form
      v-if="!visibleLoading"
      class="form work-temp-form"
      ref="formRef"
      @submit="onSubmit"
    >
      <template v-for="item in formList">
        <div v-if="isTextareaField(item)" :key="'ta-' + item.id">
          <HelpTip v-bind="item.labelInfo" />
          <van-field
            class="textarea-temp work-temp-form-item work-temp-form-item-textarea"
            v-model="formData[item.id]"
            :name="item.id"
            type="textarea"
            rows="3"
            autosize
            :clearable="true"
            :rules="
              item.required
                ? [
                    {
                      required: true,
                      message: languages.smartOrderPleaseInpt + item.label
                    }
                  ]
                : []
            "
            :placeholder="languages.smartOrderPleaseInpt + item.label"
            maxlength="600"
          />
        </div>
        <component
          :is="resolveComp(item)"
          v-else
          :key="item.id"
          :id="item.id"
          :type="item.type"
          :formData="formData"
          :labelInfo="item.labelInfo"
          :customKey="item.customKey"
          :gameId="gameId"
          :required="item.required"
          :label="item.label"
          :placeholder="resolvePlaceholder(item)"
        />
      </template>
    </van-form>

    <template v-if="!visibleLoading && !isReview" #footer>
      <div class="form-item-submit">
        <van-button
          class="btn"
          block
          type="info"
          :loading="loadingSubmit"
          @click="onFooterSubmit"
        >
          <template #icon>
            <em class="btn-icon iconfont icon-info"></em>
          </template>
          <template>{{ languages.submit }}</template>
        </van-button>
      </div>
    </template>

    <template #overlay>
      <van-overlay :show="visibleLoading">
        <div class="loading-wrapper" @click.stop>
          <van-loading color="#1989fa" size="40" />
        </div>
      </van-overlay>
    </template>
  </SupportPageCard>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import SupportPageCard from '@components/officialSupport/common/SupportPageCard.vue';
import TempSelect from '@components/officialSupport/workTemp/TempSelect.vue';
import TempInput from '@components/officialSupport/workTemp/TempInput.vue';
import TempSwitch from '@components/officialSupport/workTemp/TempSwitch.vue';
import TempDateTimePicker from '@components/officialSupport/workTemp/TempDateTimePicker.vue';
import TempUpload from '@components/officialSupport/workTemp/TempUpload.vue';
import TempOrder from '@components/officialSupport/workTemp/TempOrder.vue';
import TempAccountType from '@components/officialSupport/workTemp/TempAccountType.vue';
import TempServer from '@components/officialSupport/workTemp/TempServer.vue';
import HelpTip from '@components/officialSupport/workTemp/HelpTip.vue';
import {
  formType,
  fieldType
} from '@configs/officialSupport/workTemp';
import iframeMessage from '@mixins/officialSupport/iframeMessage';
import globalConfig from '@utiles/globalConfig';

const isReview = globalConfig.getHrefParam('isReview') == 1;
const hideBackBtn = globalConfig.getHrefParam('hideBackBtn') == 1;
/** 与 worktemp 一致：走专用组件的语义 type */
const CUSTOM_FIELD_TYPES = [
  fieldType.order,
  fieldType.accountType,
  fieldType.server
];

function normalizeOptions(options) {
  if (!Array.isArray(options)) {
    return [];
  }
  return options
    .map(item => {
      if (item && typeof item === 'object') {
        const value = item.value !== undefined ? item.value : item.id;
        const label = item.label || item.name || String(value);
        return { label, value };
      }
      return { label: String(item), value: item };
    })
    .filter(item => item.value !== undefined && item.value !== null);
}

export default {
  name: 'SelfServiceForm',
  mixins: [iframeMessage],
  components: {
    SupportPageCard,
    TempSelect,
    TempInput,
    TempSwitch,
    TempDateTimePicker,
    TempUpload,
    TempOrder,
    TempAccountType,
    TempServer,
    HelpTip
  },
  data() {
    return {
      loadingSubmit: false,
      visibleLoading: true,
      isReview,
      hideBackBtn,
      formName: '',
      formList: [],
      formData: {},
      autoReplyText: '',
      gameId:
        globalConfig.getHrefParam('gameId') ||
        globalConfig.getHrefParam('gameID') ||
        globalConfig.getHrefParam('game_id') ||
        '',
      compType: {
        [formType.input]: TempInput,
        [formType.select]: TempSelect,
        [formType.switch]: TempSwitch,
        [formType.date]: TempDateTimePicker,
        [formType.upload]: TempUpload,
        [fieldType.order]: TempOrder,
        [fieldType.accountType]: TempAccountType,
        [fieldType.server]: TempServer
      }
    };
  },
  computed: {
    ...mapState({
      languages: state => state.languages,
      curLang: state => state.curLang,
      defaultLang: state => state.defaultLang,
      isIframe: state => state.isIframe,
      urlParams: state => state.urlParams
    }),
    formId() {
      return this.$route.query.formId;
    },
    pageTitle() {
      return (
        this.formName ||
        this.$route.query.name ||
        this.languages.workHomeSelfService ||
        ''
      );
    }
  },
  watch: {
    curLang() {
      // 预览模式不调 gsupport；标题/文案用注入的 name / autoReplyText
      if (isReview) {
        return;
      }
      this.loadDetail();
    }
  },
  beforeCreate() {
    if (isReview) {
      globalConfig.addEvenListener(({ data }) => {
        if (data?.type === 'reviewData' && data?.data?.id) {
          this.applyReviewData(data.data);
        }
      }, 'message');
    }
  },
  created() {
    if (isReview) {
      this.postMessageToParent('getPrevieDate');
      return;
    }
    this.loadDetail();
  },
  mounted() {
    this.scrollSsrPageToTop();
  },
  methods: {
    ...mapActions(['getSelfServiceFormDetail', 'submitSelfServiceForm']),
    /** 进页将 #ssr-page 滚到顶部（与 workfaqdetail 一致） */
    scrollSsrPageToTop() {
      this.$nextTick(() => {
        const el = document.getElementById('ssr-page');
        if (el) el.scrollTop = 0;
      });
    },
    resolveLabel(item) {
      const multi = item?.label_multi_lang || item?.labelMultiLang || {};
      return (
        multi[this.curLang] ||
        (this.defaultLang && multi[this.defaultLang]) ||
        multi['en-US'] ||
        item?.label ||
        item?.name ||
        ''
      );
    },
    resolveTip(item) {
      const multi = item?.tip_multi_lang || item?.tipMultiLang || {};
      return (
        multi[this.curLang] ||
        (this.defaultLang && multi[this.defaultLang]) ||
        multi['en-US'] ||
        item?.tip ||
        ''
      );
    },
    isTextareaField(item) {
      const formTypeVal = String(item.formType || '').toLowerCase();
      const typeVal = String(item.type || '').toLowerCase();
      return formTypeVal === 'textarea' || typeVal === 'textarea';
    },
    resolveComp(item) {
      if (item.isCustomComp) {
        return this.compType[item.type] || TempInput;
      }
      return this.compType[item.formType] || TempInput;
    },
    resolvePlaceholder(item) {
      const ft = String(item.formType || '').toLowerCase();
      if (ft === formType.select || item.type === fieldType.server) {
        return this.languages.smartOrderPleaseChoose + item.label;
      }
      if (ft === formType.date) {
        return this.languages.smartOrderPleaseChoose + item.label;
      }
      return this.languages.smartOrderPleaseInpt + item.label;
    },
    /**
     * 归一化 formType：语义字段缺省时与 worktemp 常见配置对齐
     * e.g. type=server → select；type=regTime → date；roleName/account/device → input
     */
    resolveFormType(type, formTypeRaw) {
      const ft = String(formTypeRaw || '').trim();
      if (ft) {
        return ft;
      }
      if (type === fieldType.server) {
        return formType.select;
      }
      if (type === fieldType.regTime) {
        return formType.date;
      }
      if (
        type === fieldType.upload ||
        type === 'image' ||
        type === 'file'
      ) {
        return formType.upload;
      }
      if (
        type === fieldType.roleName ||
        type === fieldType.account ||
        type === fieldType.device ||
        type === fieldType.address ||
        type === fieldType.customer
      ) {
        return formType.input;
      }
      if (type === 'textarea') {
        return 'textarea';
      }
      if (type === 'select' || type === 'switch' || type === 'date') {
        return type;
      }
      return formType.input;
    },
    buildCustomKey(item, type, resolvedFormType) {
      const src =
        item.customKey && typeof item.customKey === 'object'
          ? { ...item.customKey }
          : {};
      if (!Array.isArray(src.options)) {
        src.options = normalizeOptions(item.options);
      } else {
        src.options = normalizeOptions(src.options);
      }
      // 日期字段：picker type，缺省与 TempDateTimePicker 一致
      if (resolvedFormType === formType.date && !src.type) {
        src.type = item.dateType || item.timeFormat || 'year-month';
      }
      // server 由 TempServer 自己拉列表，忽略配置里的静态 options
      if (type === fieldType.server) {
        src.options = [];
      }
      return src;
    },
    parseFields(configJson) {
      let config = configJson;
      if (typeof configJson === 'string') {
        try {
          config = JSON.parse(configJson);
        } catch (e) {
          return [];
        }
      }
      if (!config || typeof config !== 'object') {
        return [];
      }
      const rawFields = Array.isArray(config.fields)
        ? config.fields
        : Array.isArray(config.questions)
          ? config.questions.map(item => ({
              ...item,
              key: item.key || item.id
            }))
          : [];
      return rawFields
        .map(item => {
          const type = String(item.type || fieldType.customer);
          const key = item.key || item.id;
          if (!key) {
            return null;
          }
          const resolvedFormType = this.resolveFormType(type, item.formType);
          const isCustomComp = CUSTOM_FIELD_TYPES.includes(type);
          const label = String(this.resolveLabel(item));
          const tip = String(this.resolveTip(item));
          const required = !!item.required;
          return {
            id: String(key),
            type,
            formType: resolvedFormType,
            isCustomComp,
            required,
            label,
            tip,
            labelInfo: {
              label,
              tip,
              required,
              tipImg: item.tipImg || item.tip_img || []
            },
            customKey: this.buildCustomKey(item, type, resolvedFormType)
          };
        })
        .filter(Boolean);
    },
    /** Boss 预览：用 postMessage 注入数据渲染，不调 getSelfServiceFormDetail */
    applyReviewData(reviewData) {
      this.visibleLoading = true;
      this.formName = reviewData.name || this.$route.query.name || '';
      this.autoReplyText =
        reviewData.autoReplyText || reviewData.auto_reply_text || '';
      const fields = this.parseFields(
        reviewData.configJson || reviewData.config_json
      );
      const formData = {};
      fields.forEach(item => {
        formData[item.id] = item.isCustomComp ? {} : null;
      });
      this.formList = fields;
      this.formData = formData;
      this.visibleLoading = false;
      this.scrollSsrPageToTop();
    },
    async loadDetail() {
      if (!this.formId) {
        this.$toast(this.languages.smartOrderComing);
        this.goBack();
        return;
      }
      this.visibleLoading = true;
      try {
        const data = await this.getSelfServiceFormDetail({
          formId: this.formId,
          gameId: this.urlParams.gameId || this.gameId
        });
        this.formName =
          data.name || data.inside_name || this.$route.query.name || '';
        this.autoReplyText =
          data.autoReplyText || data.auto_reply_text || '';
        // 兼容 configJson 包装和接口直接返回 questions 两种结构
        const fields = this.parseFields(
          data.configJson || data.config_json || data
        );
        const formData = {};
        fields.forEach(item => {
          // 与 worktemp 一致：普通字段 null，由各组件写入；自定义复合字段用对象占位
          formData[item.id] = item.isCustomComp ? {} : null;
        });
        this.formList = fields;
        this.formData = formData;
        this.scrollSsrPageToTop();
      } catch (e) {
        this.$toast(e || this.languages.smartOrderComing);
        this.goBack();
        return;
      }
      this.visibleLoading = false;
    },
    onFooterSubmit() {
      if (isReview) {
        return;
      }
      this.$refs.formRef && this.$refs.formRef.submit();
    },
    resolveMsg(msg) {
      if (!msg) {
        return '';
      }
      return this.languages[msg] || msg;
    },
    formatSubmitValue(value) {
      if (value == null) {
        return '';
      }
      if (typeof value === 'object') {
        // 上传、订单、账号类型等复合字段需完整提交给后端
        try {
          return JSON.stringify(value);
        } catch (e) {
          return '';
        }
      }
      return String(value);
    },
    async onSubmit() {
      // 预览模式禁止提交
      if (isReview) {
        return;
      }
      if (this.loadingSubmit) {
        return;
      }
      this.loadingSubmit = true;
      try {
        const submitContent = this.formList.map(item => ({
          fieldKey: item.id,
          fieldName: item.label,
          value: this.formatSubmitValue(this.formData[item.id])
        }));
        const res = await this.submitSelfServiceForm({
          formId: this.formId,
          gameId: this.urlParams.gameId || this.gameId,
          submitContentJson: JSON.stringify(submitContent)
        });
        if (res?.result === 1) {
          const data = res.data || {};
          const reply =
            data.auto_reply_text || data.autoReplyText || this.autoReplyText;
          const orderId = Number(data.order_id || data.orderId || 0);
          const tips = [];
          if (reply) {
            tips.push(reply);
          }
          if (orderId > 0) {
            tips.push(
              `${this.languages.selfServiceWorkId || ''}: ${orderId}`
            );
          }
          this.$toast(tips.join('\n') || this.languages.submitSuccess);
          this.$router.push('/workhome');
          return;
        }
        this.$toast(this.resolveMsg(res?.msg) || this.languages.pwaCommon);
      } catch (e) {
        this.$toast(this.languages.pwaCommon || String(e));
      }
      this.loadingSubmit = false;
    },
    goBack() {
      if (this.isIframe) {
        this.postMessageToParent('goBack');
      } else if (globalConfig.isTiktokMiniProgram()) {
        this.$router.push('/workhome');
      } else {
        this.$router.go(-1);
      }
    }
  }
};
</script>
